import type { IIssue, IIssueComment, IssueDependencyType, IssueImportHeaderMapping, IssueLinkType } from '@pulse/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addIssueComment,
  addIssueLink,
  addIssueWorkLog,
  activateIssue,
  commitIssueImport,
  createIssue,
  deactivateIssue,
  fetchIssue,
  fetchIssueComments,
  fetchIssueHistory,
  fetchIssueImportMapping,
  fetchIssues,
  fetchIssueWorkLogs,
  followIssue,
  inspectIssueImport,
  removeIssueLink,
  saveIssueImportMapping,
  transitionIssue,
  unfollowIssue,
  updateIssue,
  updateIssueComment,
  updateIssueWorkLog,
  validateIssueImport,
  type IssueImportOptions,
  type IssueInput,
} from '../api/issue.api';

export const ISSUE_KEYS = {
  list: (projectId: string) => ['issues', projectId] as const,
  detail: (projectId: string, issueId: string) => ['issues', projectId, issueId] as const,
  comments: (projectId: string, issueId: string) => ['issues', projectId, issueId, 'comments'] as const,
  workLogs: (projectId: string, issueId: string) => ['issues', projectId, issueId, 'work-logs'] as const,
  history: (projectId: string, issueId: string, limit: number, offset: number) =>
    ['issues', projectId, issueId, 'history', limit, offset] as const,
};

export function useIssues(projectId: string) {
  return useQuery({
    queryKey: ISSUE_KEYS.list(projectId),
    queryFn: () => fetchIssues(projectId),
    enabled: Boolean(projectId),
    // staleTime: 0 — always refetch on window focus / tab switch so deactivations by other
    // users are reflected promptly without requiring a manual page refresh.
    staleTime: 0,
  });
}

export function useIssue(projectId: string, issueId: string) {
  return useQuery({
    queryKey: ISSUE_KEYS.detail(projectId, issueId),
    queryFn: () => fetchIssue(projectId, issueId),
    enabled: Boolean(projectId) && Boolean(issueId),
  });
}

function invalidateIssue(queryClient: ReturnType<typeof useQueryClient>, projectId: string, issueId?: string) {
  void queryClient.invalidateQueries({ queryKey: ISSUE_KEYS.list(projectId) });
  if (issueId) void queryClient.invalidateQueries({ queryKey: ISSUE_KEYS.detail(projectId, issueId) });
}

export function useCreateIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: IssueInput }) => createIssue(projectId, data),
    onSuccess: (_result, { projectId }) => invalidateIssue(queryClient, projectId),
  });
}

export function useUpdateIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      issueId,
      data,
    }: {
      projectId: string;
      issueId: string;
      data: Partial<IssueInput>;
    }) => updateIssue(projectId, issueId, data),
    onSuccess: (updated, { projectId }) => {
      // Write the mutation's own response straight into the detail + list caches instead of
      // only invalidating and waiting on a second round-trip GET — the PATCH response is
      // already the same toIssueResponse() shape a refetch would return, so this makes every
      // view (IssueDetailDialog's own useIssue, plus IssueLinkPage/Board/Planner which all read
      // from the list) reflect the saved value immediately instead of sitting on stale data
      // until the refetch lands. loggedHours is preserved from the cached item rather than
      // taken from `updated` — the update endpoint's response doesn't recompute that aggregate
      // (see toIssueResponse's optional loggedHours param), so overwriting it here would
      // momentarily blank out logged time in the Board/Gantt views.
      queryClient.setQueryData(ISSUE_KEYS.detail(projectId, updated.id), updated);
      queryClient.setQueryData<IIssue[]>(ISSUE_KEYS.list(projectId), (old) =>
        old?.map((i) => (i.id === updated.id ? { ...updated, loggedHours: i.loggedHours } : i)),
      );
      void queryClient.invalidateQueries({ queryKey: ISSUE_KEYS.list(projectId) });
    },
  });
}

export function useTransitionIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, issueId, toStep }: { projectId: string; issueId: string; toStep: string }) =>
      transitionIssue(projectId, issueId, toStep),
    onSuccess: (_result, { projectId, issueId }) => invalidateIssue(queryClient, projectId, issueId),
  });
}

export function useDeactivateIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, issueId }: { projectId: string; issueId: string }) =>
      deactivateIssue(projectId, issueId),
    onSuccess: (_result, { projectId }) => {
      // Invalidate the full issue list so the deactivated issue disappears from Board/Gantt/Issues tab.
      invalidateIssue(queryClient, projectId);
      // Also invalidate timesheet and home-dashboard caches — deactivated issues must
      // disappear from the Timesheet view immediately without requiring a page refresh.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useActivateIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, issueId }: { projectId: string; issueId: string }) =>
      activateIssue(projectId, issueId),
    onSuccess: (_result, { projectId }) => invalidateIssue(queryClient, projectId),
  });
}

export function useAddIssueLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      issueId,
      linkedIssueId,
      linkType,
      dependencyType,
    }: {
      projectId: string;
      issueId: string;
      linkedIssueId: string;
      linkType: IssueLinkType;
      dependencyType?: IssueDependencyType;
    }) => addIssueLink(projectId, issueId, linkedIssueId, linkType, dependencyType),
    onSuccess: (_result, { projectId, issueId }) => invalidateIssue(queryClient, projectId, issueId),
  });
}

export function useRemoveIssueLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, issueId, linkId }: { projectId: string; issueId: string; linkId: string }) =>
      removeIssueLink(projectId, issueId, linkId),
    onSuccess: (_result, { projectId, issueId }) => invalidateIssue(queryClient, projectId, issueId),
  });
}

export function useToggleFollowIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, issueId, follow }: { projectId: string; issueId: string; follow: boolean }) =>
      follow ? followIssue(projectId, issueId) : unfollowIssue(projectId, issueId),
    onSuccess: (_result, { projectId, issueId }) => invalidateIssue(queryClient, projectId, issueId),
  });
}

export function useIssueComments(projectId: string, issueId: string) {
  return useQuery({
    queryKey: ISSUE_KEYS.comments(projectId, issueId),
    queryFn: () => fetchIssueComments(projectId, issueId),
    enabled: Boolean(projectId) && Boolean(issueId),
  });
}

export function useAddIssueComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, issueId, body }: { projectId: string; issueId: string; body: string }) =>
      addIssueComment(projectId, issueId, body),
    onSuccess: (_result, { projectId, issueId }) =>
      queryClient.invalidateQueries({ queryKey: ISSUE_KEYS.comments(projectId, issueId) }),
  });
}

export function useUpdateIssueComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      issueId,
      commentId,
      body,
    }: {
      projectId: string;
      issueId: string;
      commentId: string;
      body: string;
    }) => updateIssueComment(projectId, issueId, commentId, body),
    onSuccess: (updated, { projectId, issueId }) => {
      // Same rationale as useUpdateIssue: patch the already-fetched comment straight into the
      // cached list instead of waiting on an invalidated refetch to show the saved edit.
      queryClient.setQueryData<IIssueComment[]>(ISSUE_KEYS.comments(projectId, issueId), (old) =>
        old?.map((c) => (c.id === updated.id ? updated : c)),
      );
    },
  });
}

export function useIssueWorkLogs(projectId: string, issueId: string) {
  return useQuery({
    queryKey: ISSUE_KEYS.workLogs(projectId, issueId),
    queryFn: () => fetchIssueWorkLogs(projectId, issueId),
    enabled: Boolean(projectId) && Boolean(issueId),
  });
}

// ── History (audit trail) ──────────────────────────────────────────────────────
export function useIssueHistory(projectId: string, issueId: string, limit = 25, offset = 0) {
  return useQuery({
    queryKey: ISSUE_KEYS.history(projectId, issueId, limit, offset),
    queryFn: () => fetchIssueHistory(projectId, issueId, { limit, offset }),
    enabled: Boolean(projectId) && Boolean(issueId),
  });
}

// A work log entry feeds four independent caches beyond its own issue's work-log list: Home's
// "my issues" totalLoggedHours + weekly time summary (HOME_KEYS, both prefixed 'me' —
// apps/web/src/features/home/hooks/useHome.ts), the Timesheet week grid (TIME_KEYS, prefixed
// 'timesheet' — apps/web/src/features/time/hooks/useTimesheet.ts), and the project's issue list
// (IIssue.loggedHours, aggregated server-side in listForProject — feeds the Gantt view's
// progress indicator). Invalidating by those key prefixes here — once, centrally — means every
// current and future work-log entry point (Home's LogTimeDialog, IssueDetailDialog's Work Log
// tab, the Timesheet grid's WorkLogPanel, and Resource Levelling's actual-hours grid) stays in
// sync without each call site re-deriving which other screens might be showing stale data.
function invalidateWorkLogQueries(queryClient: ReturnType<typeof useQueryClient>, projectId: string, issueId: string) {
  void queryClient.invalidateQueries({ queryKey: ISSUE_KEYS.workLogs(projectId, issueId) });
  void queryClient.invalidateQueries({ queryKey: ISSUE_KEYS.list(projectId) });
  void queryClient.invalidateQueries({ queryKey: ['me'] });
  void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
  void queryClient.invalidateQueries({ queryKey: ['resource-workload'] });
}

export function useAddIssueWorkLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      issueId,
      data,
    }: {
      projectId: string;
      issueId: string;
      data: { hours: number; description?: string; date: string };
    }) => addIssueWorkLog(projectId, issueId, data),
    onSuccess: (_result, { projectId, issueId }) => invalidateWorkLogQueries(queryClient, projectId, issueId),
  });
}

export function useUpdateIssueWorkLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      issueId,
      workLogId,
      data,
    }: {
      projectId: string;
      issueId: string;
      workLogId: string;
      data: { hours?: number; description?: string | null; date?: string };
    }) => updateIssueWorkLog(projectId, issueId, workLogId, data),
    onSuccess: (_result, { projectId, issueId }) => invalidateWorkLogQueries(queryClient, projectId, issueId),
  });
}

// ── Import from Excel ─────────────────────────────────────────────────────────
// The caller's own remembered column mapping for this project — fetched once when the import
// dialog mounts so it's ready by the time a file is picked; no cache invalidation, nothing else
// reads this.
export function useIssueImportMapping(projectId: string) {
  return useQuery({
    queryKey: ['issueImportMapping', projectId],
    queryFn: () => fetchIssueImportMapping(projectId),
    enabled: Boolean(projectId),
    staleTime: Infinity,
  });
}

export function useSaveIssueImportMapping() {
  return useMutation({
    mutationFn: ({ projectId, mapping }: { projectId: string; mapping: IssueImportHeaderMapping }) =>
      saveIssueImportMapping(projectId, mapping),
  });
}

export function useInspectIssueImport() {
  return useMutation({
    mutationFn: ({ projectId, file, sheet }: { projectId: string; file: File; sheet?: string }) => inspectIssueImport(projectId, file, sheet),
  });
}

export function useValidateIssueImport() {
  return useMutation({
    mutationFn: ({ projectId, file, ...opts }: { projectId: string; file: File } & IssueImportOptions) =>
      validateIssueImport(projectId, file, opts),
  });
}

export function useCommitIssueImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      file,
      importAnyway,
      updateRows,
      ...opts
    }: {
      projectId: string;
      file: File;
      importAnyway: number[];
      updateRows: number[];
    } & IssueImportOptions) => commitIssueImport(projectId, file, importAnyway, updateRows, opts),
    // Even a partial result (some rows failed) has created issues — refresh the list regardless.
    onSuccess: (_result, { projectId }) => invalidateIssue(queryClient, projectId),
  });
}
